import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeft, ClipboardPen, EyeOff, History, Info, Printer } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { downloadCollectionSheet } from '@/api/documents-api'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { StatusBadge } from '@/components/status-badge'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { BusinessDayGate } from '@/features/collections/business-day-gate'
import { CollectionGrid } from '@/features/collections/collection-grid'
import { CollectionsLink } from '@/features/collections/collections-link'
import {
  COLUMN_DESCRIPTIONS,
  COLUMN_LABELS,
  COLLECTION_COLUMNS,
  formatMeetingTime,
  isCapturable,
  isValueDateAllowed,
  type CollectionColumn,
} from '@/features/collections/constants'
import {
  COLLECTION_PERMISSIONS,
  useCollectionPermissions,
} from '@/features/collections/permissions'
import { PostBatchDialog } from '@/features/collections/post-batch-dialog'
import {
  useCollectionBusinessDay,
  useGroupLoanAccounts,
  useMemberSavingsMap,
} from '@/features/collections/queries'
import { ReconciliationPanel } from '@/features/collections/reconciliation-panel'
import {
  useCollectionNavigate,
  useCollectionSearch,
  useCollectionSearchNavigate,
} from '@/features/collections/routing'
import {
  clearJournal,
  journalAvailable,
  readJournal,
  writeJournal,
  type SheetJournal,
} from '@/features/collections/sheet-journal'
import {
  amountKey,
  buildSheet,
  countPostableLines,
  membersOnDate,
  objections as findObjections,
  totalsFor,
  type SheetAmounts,
} from '@/features/collections/sheet-model'
import { ViewDocumentButton } from '@/components/document-viewer'
import { SheetTotalsBar } from '@/features/collections/sheet-totals-bar'
import {
  prepareLines,
  useBatchPoster,
  type BatchContext,
  type PreparedLine,
} from '@/features/collections/use-batch-poster'
import { ValueDateField } from '@/features/collections/value-date-field'
import { useGroup, useGroupMeeting } from '@/features/groups/queries'
import { formatShortDate } from '@/lib/datetime'
import type { Pesewas } from '@/lib/money'

import { collectionSheetSearchSchema, type CollectionSheetTab } from './search'

const PANEL_ID = 'collection-sheet-panel'

/**
 * The collection sheet.
 *
 * This is the screen the release is judged on: a branch's whole week of paper reaches the system
 * through it, keyed by an operator working down a grid with both hands on the keyboard. Three
 * decisions carry the design.
 *
 * **It refuses a narrow display.** `docs/mobile-policy.md` names this screen specifically, and the
 * reason is exact rather than aesthetic: a dense grid worked at speed is the one thing touch turns
 * from this screen's advantage into its risk, and the failure it produces is a mis-keyed cash
 * amount against a real customer's account, found at end-of-day reconciliation. Attendance capture
 * sits next to this on every superficial measure and does *not* refuse, because choosing among
 * four states is what touch is best at and keying an amount is what it is worst at.
 *
 * **It checks before it commits.** The officer's declared total from the bottom of the paper sheet
 * is entered beside the running total, and the two agreeing is the evidence that forty amounts
 * were transcribed correctly. Lines the server will certainly refuse are named on the sheet rather
 * than discovered on the ninth request.
 *
 * **It does not pretend to be atomic.** The contract has no endpoint that takes a sheet, so a
 * batch is a run of individual postings and a refusal part-way leaves the earlier lines posted.
 * The screen says that before the button is pressed, posts in sheet order, carries on past a
 * refusal, and gives every line an idempotency key so the sheet can be re-run without taking the
 * same money twice.
 */
export function CollectionSheetPage() {
  const params = useParams({ strict: false })
  const groupId = params.groupId ?? ''
  const meetingId = params.meetingId ?? ''

  const permissions = useCollectionPermissions()
  const search = useCollectionSearch(collectionSheetSearchSchema)
  const navigateWithSearch = useCollectionSearchNavigate()
  const navigate = useCollectionNavigate()
  const tab = search.tab ?? 'sheet'

  const group = useGroup(groupId)
  const meetingQuery = useGroupMeeting(groupId, meetingId)
  const businessDay = useCollectionBusinessDay()
  const meeting = meetingQuery.data ?? null

  const eligible = useMemo(
    () =>
      group.data && meeting ? membersOnDate(group.data.members, meeting.scheduledDate) : [],
    [group.data, meeting],
  )

  const loanAccounts = useGroupLoanAccounts(groupId, permissions.canViewLoans)
  const savingsByCustomer = useMemberSavingsMap(eligible, permissions.canViewSavings)

  const [amounts, setAmounts] = useState<SheetAmounts>({})
  const [declaredMinor, setDeclaredMinor] = useState<Pesewas | null>(null)
  /**
   * The difference the operator actually acknowledged, rather than a bare "yes".
   *
   * A tick against a shortfall of ₵40 is not consent to a shortfall of ₵400. Keying another line
   * or correcting the declared total moves the difference, and the acknowledgement has to be taken
   * again for the new figure — which is what holding the amount rather than a boolean gives.
   */
  const [acknowledgedVarianceMinor, setAcknowledgedVarianceMinor] = useState<Pesewas | null>(null)
  const [valueDate, setValueDate] = useState<string | null>(null)
  const [journal, setJournal] = useState<SheetJournal>(() => readJournal(meetingId))
  const [pendingLines, setPendingLines] = useState<PreparedLine[]>([])
  const [reviewOpen, setReviewOpen] = useState(false)
  const [confirmingFreshSheet, setConfirmingFreshSheet] = useState(false)

  const poster = useBatchPoster(meetingId)
  const storageWorks = useMemo(() => journalAvailable(), [])

  const businessDate = businessDay.data?.businessDate ?? null

  /**
   * The value date starts at the day the meeting was held, because that is when the money moved —
   * not the day somebody sat down to key it. Where the meeting is further back than the backdating
   * window allows, it falls to the branch's own day, and the field says why.
   */
  useEffect(() => {
    if (valueDate !== null || businessDate === null || meeting === null) {
      return
    }

    setValueDate(
      isValueDateAllowed(meeting.scheduledDate, businessDate)
        ? meeting.scheduledDate
        : businessDate,
    )
  }, [valueDate, businessDate, meeting])

  /** Columns this operator can do anything with at all. The rest are named, not silently dropped. */
  const visibleColumns = useMemo<CollectionColumn[]>(
    () =>
      COLLECTION_COLUMNS.filter((column) => {
        if (column === 'LoanRepayment') {
          return permissions.canViewLoans || permissions.canPostRepayment
        }
        if (column === 'SavingsDeposit') {
          return permissions.canViewSavings || permissions.canPostDeposit
        }
        return permissions.canViewSavings || permissions.canTopUpSecurityDeposit
      }),
    [permissions],
  )

  const hiddenColumns = COLLECTION_COLUMNS.filter((column) => !visibleColumns.includes(column))

  const rows = useMemo(() => {
    if (!group.data || !meeting) {
      return []
    }

    return buildSheet({
      group: group.data,
      meeting,
      loanAccounts: loanAccounts.data?.items ?? null,
      loansPending: permissions.canViewLoans && loanAccounts.isPending,
      loansUnreadable: loanAccounts.isError,
      savingsByCustomer,
      permissions,
      permissionCodes: {
        loanView: COLLECTION_PERMISSIONS.loanView,
        repaymentPost: COLLECTION_PERMISSIONS.repaymentPost,
        savingsView: COLLECTION_PERMISSIONS.savingsView,
        depositPost: COLLECTION_PERMISSIONS.depositPost,
        securityDepositTopUp: COLLECTION_PERMISSIONS.securityDepositTopUp,
      },
    })
  }, [
    group.data,
    meeting,
    loanAccounts.data,
    loanAccounts.isError,
    loanAccounts.isPending,
    savingsByCustomer,
    permissions,
  ])

  const totals = useMemo(() => totalsFor(rows, amounts), [rows, amounts])
  const objections = useMemo(() => findObjections(rows, amounts), [rows, amounts])
  const objectionKeys = useMemo(
    () => new Set(objections.map((objection) => amountKey(objection.customerId, objection.column))),
    [objections],
  )

  const postedKeys = useMemo(() => new Set(Object.keys(journal.posted)), [journal])
  const postableCount = useMemo(
    () => countPostableLines(rows, amounts, postedKeys),
    [rows, amounts, postedKeys],
  )

  const setAmount = (customerId: string, column: CollectionColumn, value: Pesewas | null) => {
    setAmounts((current) => ({ ...current, [amountKey(customerId, column)]: value }))
  }

  const varianceMinor = declaredMinor === null ? null : declaredMinor - totals.capturedMinor
  const varianceAcknowledged =
    varianceMinor !== null && acknowledgedVarianceMinor === varianceMinor
  const dayAcceptsPostings = businessDay.data?.acceptsPostings === true && businessDate !== null
  const valueDateValid =
    valueDate !== null && businessDate !== null && isValueDateAllowed(valueDate, businessDate)

  const blockedReason = ((): string | null => {
    if (!permissions.canPostAnything) {
      return 'Your role holds none of the three posting permissions.'
    }
    if (!dayAcceptsPostings) {
      return 'The branch day is not taking postings.'
    }
    if (!valueDateValid) {
      return 'The value date is outside the permitted window.'
    }
    if (totals.lineCount === 0) {
      return 'Nothing has been keyed yet.'
    }
    if (postableCount === 0) {
      return 'Everything keyed on this sheet has already posted.'
    }
    if (objections.length > 0) {
      return `${String(objections.length)} line${objections.length === 1 ? '' : 's'} would be refused — see reconciliation.`
    }
    if (declaredMinor === null) {
      return 'Enter the officer’s declared total from the bottom of the sheet.'
    }
    if (varianceMinor !== 0 && !varianceAcknowledged) {
      return 'The totals differ. Acknowledge the difference to continue.'
    }
    return null
  })()

  const context: BatchContext | null =
    group.data && meeting && valueDate !== null
      ? {
          groupId,
          groupCode: group.data.groupCode,
          meetingId,
          meetingDate: meeting.scheduledDate,
          valueDate,
        }
      : null

  const openReview = () => {
    if (!context) {
      return
    }

    // Read from storage rather than from React state. The poster writes each line into the journal
    // the moment it settles, and a run whose dialog was closed part-way leaves this component's
    // copy behind — so preparing from state would put lines that have already posted back into the
    // review list as though they were still to be sent.
    const current = readJournal(meetingId)
    const prepared = prepareLines(rows, amounts, context, current)
    // The operation ids are written down before the first request, not after it: a browser closed
    // mid-run must leave behind the keys that make a re-run idempotent.
    setJournal(prepared.journal)
    writeJournal(meetingId, prepared.journal)
    setPendingLines(prepared.lines)
    poster.reset()
    setReviewOpen(true)
  }

  const alreadyPosted = journal.posted

  const backLink = (
    <CollectionsLink
      destination={{ screen: 'groupMeetings', groupId }}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
    >
      <ArrowLeft className="size-3.5" aria-hidden />
      Back to this group&apos;s meetings
    </CollectionsLink>
  )

  const body = (() => {
    if (!permissions.canViewGroups) {
      return (
        <Alert tone="warning">
          <AlertTitle>This sheet cannot be read</AlertTitle>
          <AlertDescription>
            A collection sheet is built from a group&apos;s membership and its meeting calendar, so
            it needs <span className="font-mono text-xs">{COLLECTION_PERMISSIONS.groupView}</span>.
          </AlertDescription>
        </Alert>
      )
    }

    if (group.isPending || meetingQuery.isPending || businessDay.isPending) {
      return (
        <div className="space-y-4">
          <Skeleton className="h-10 w-96" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      )
    }

    if (group.isError) {
      return (
        <ErrorSurface
          error={group.error}
          onRetry={() => void group.refetch()}
          title="This group could not be opened"
        />
      )
    }

    if (meetingQuery.isError) {
      return (
        <ErrorSurface
          error={meetingQuery.error}
          onRetry={() => void meetingQuery.refetch()}
          title="The meeting calendar could not be read"
        />
      )
    }

    if (businessDay.isError) {
      return (
        <ErrorSurface
          error={businessDay.error}
          onRetry={() => void businessDay.refetch()}
          title="The branch business day could not be read"
        />
      )
    }

    if (meeting === null) {
      return (
        <Alert tone="warning">
          <AlertTitle>That meeting is not on this group&apos;s calendar</AlertTitle>
          <AlertDescription>
            It may have been cancelled by a schedule change, or the link may belong to another
            group. There is no sheet to key against it.
          </AlertDescription>
        </Alert>
      )
    }

    if (!isCapturable(meeting.status)) {
      return (
        <Alert tone="warning">
          <AlertTitle>This meeting was cancelled</AlertTitle>
          <AlertDescription>
            A schedule change cancels meetings the previous schedule had put on the calendar where
            they had not yet happened. Nothing was collected at one, so there is no sheet.
          </AlertDescription>
        </Alert>
      )
    }

    return (
      <>
        <PageHeader
          title={`${group.data.name} — ${formatShortDate(meeting.scheduledDate)}`}
          reference={group.data.groupCode}
          badges={<StatusBadge status={meeting.status} />}
          description={`Round ${String(meeting.roundNumber)}, scheduled for ${formatMeetingTime(meeting.scheduledTime)}. ${String(rows.length)} member${rows.length === 1 ? '' : 's'} belonged to this group on the day.`}
          actions={
            <ViewDocumentButton
              variant="secondary"
              title={`Collection sheet — ${group.data.name}, ${formatShortDate(meeting.scheduledDate)}`}
              load={() => downloadCollectionSheet(meetingId)}
            >
              <Printer />
              Print collection sheet
            </ViewDocumentButton>
          }
        />

        {businessDay.data ? <BusinessDayGate day={businessDay.data} /> : null}

        {hiddenColumns.length > 0 ? (
          <Alert tone="info">
            <EyeOff aria-hidden />
            <div className="space-y-1">
              <AlertTitle>
                {hiddenColumns.length === 1 ? 'One column is' : `${String(hiddenColumns.length)} columns are`}{' '}
                not shown
              </AlertTitle>
              <AlertDescription>
                <ul className="space-y-0.5">
                  {hiddenColumns.map((column) => (
                    <li key={column}>
                      <span className="font-medium text-heading">{COLUMN_LABELS[column]}</span> —
                      your role holds neither the permission to read it nor the permission to post
                      it. Amounts in that column have to be keyed by somebody who does; they are
                      not absent from the sheet in front of you.
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </div>
          </Alert>
        ) : null}

        {meeting.status === 'Scheduled' || meeting.status === 'InProgress' ? (
          <Alert tone="info">
            <ClipboardPen aria-hidden />
            <div className="space-y-1">
              <AlertTitle>The register has not been recorded for this meeting</AlertTitle>
              <AlertDescription>
                The money can be keyed either way — it was collected whether or not the attendance
                sheet has been transcribed. Recording the register while the paper is in front of
                you is the cheap moment, and the attendance ratio it feeds is one of the three
                inputs to the group&apos;s health score.
                {permissions.canRecordAttendance ? (
                  <>
                    {' '}
                    <Link
                      to="/groups/$groupId/meetings/$meetingId"
                      params={{ groupId, meetingId }}
                      className="font-medium text-brand-text underline underline-offset-2 hover:text-brand-text-hover"
                    >
                      Record the register
                    </Link>
                    .
                  </>
                ) : null}
              </AlertDescription>
            </div>
          </Alert>
        ) : null}

        {Object.keys(alreadyPosted).length > 0 ? (
          <Alert tone="warning">
            <History aria-hidden />
            <div className="space-y-1">
              <AlertTitle>
                {Object.keys(alreadyPosted).length} line
                {Object.keys(alreadyPosted).length === 1 ? '' : 's'} from this sheet already posted
              </AlertTitle>
              <AlertDescription>
                This browser posted them earlier in this sitting, so they are locked and their
                amounts are not re-sent. If this is genuinely a second sheet for the same meeting,
                start a fresh one — that forgets the keys that make a re-run safe, so a line posted
                before could be posted again.
                <span className="mt-2 block">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmingFreshSheet(true)}
                  >
                    Start a fresh sheet
                  </Button>
                </span>
              </AlertDescription>
            </div>
          </Alert>
        ) : null}

        {!storageWorks ? (
          <Alert tone="warning">
            <Info aria-hidden />
            <div className="space-y-1">
              <AlertTitle>This browser cannot remember what has been posted</AlertTitle>
              <AlertDescription>
                Session storage is unavailable — a private window, or a locked-down profile. The
                sheet works, but if this tab is reloaded part-way through a batch the idempotency
                keys are lost and a re-run could post a line a second time. Finish the sheet in one
                sitting, and check the ledger before re-keying anything.
              </AlertDescription>
            </div>
          </Alert>
        ) : null}

        <Card>
          <CardContent className="flex flex-wrap items-end gap-6 p-4">
            {businessDate === null ? (
              <p className="text-sm text-muted">
                There is no branch day to value-date against yet.
              </p>
            ) : (
              <ValueDateField
                value={valueDate ?? businessDate}
                onChange={setValueDate}
                businessDate={businessDate}
                meetingDate={meeting.scheduledDate}
                disabled={poster.isPosting}
              />
            )}

            <div className="min-w-0 max-w-xl space-y-1">
              <p className="text-2xs font-semibold tracking-wide text-muted uppercase">
                What each column does
              </p>
              <ul className="space-y-0.5 text-xs text-muted">
                {visibleColumns.map((column) => (
                  <li key={column}>
                    <span className="font-medium text-heading">{COLUMN_LABELS[column]}</span> —{' '}
                    {COLUMN_DESCRIPTIONS[column]}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <TabStrip<CollectionSheetTab>
            tabs={
              [
                { id: 'sheet', label: 'Sheet', badge: totals.lineCount },
                {
                  id: 'reconciliation',
                  label: 'Reconciliation',
                  badge: objections.length > 0 ? objections.length : undefined,
                },
              ] satisfies TabDefinition<CollectionSheetTab>[]
            }
            active={tab}
            onChange={(next) => {
              navigateWithSearch({ screen: 'sheet', groupId, meetingId }, { tab: next }, true)
            }}
            panelId={PANEL_ID}
            label="Collection sheet sections"
          />

          <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
            {tab === 'sheet' ? (
              rows.length === 0 ? (
                <Alert tone="warning">
                  <AlertTitle>
                    Nobody was a member of this group on {formatShortDate(meeting.scheduledDate)}
                  </AlertTitle>
                  <AlertDescription>
                    A sheet is shaped to the meeting&apos;s own date rather than to today&apos;s
                    membership, so somebody who joined afterwards is not on it. There is nothing to
                    key.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-4">
                  {loanAccounts.isError ? (
                    <ErrorSurface
                      error={loanAccounts.error}
                      onRetry={() => void loanAccounts.refetch()}
                      title="The group's loan rounds could not be read"
                    />
                  ) : null}

                  <CollectionGrid
                    rows={rows}
                    columns={visibleColumns}
                    amounts={amounts}
                    onAmountChange={setAmount}
                    results={poster.results}
                    alreadyPosted={alreadyPosted}
                    // Only a run in flight locks the grid. A branch day that is not taking
                    // postings blocks the *commit*, and `blockedReason` says so on the button —
                    // but the panel above promises the sheet can still be keyed and checked, and
                    // a disabled grid would make that promise false. Keying a sheet at seven in
                    // the morning and committing it when the day opens is the ordinary shape of
                    // this work, not an edge case.
                    disabled={poster.isPosting}
                    objectionKeys={objectionKeys}
                  />

                  <SheetTotalsBar
                    totals={totals}
                    columns={visibleColumns}
                    declaredMinor={declaredMinor}
                    onDeclaredChange={setDeclaredMinor}
                    varianceAcknowledged={varianceAcknowledged}
                    onVarianceAcknowledgedChange={(next) => {
                      setAcknowledgedVarianceMinor(next ? varianceMinor : null)
                    }}
                    canPost={blockedReason === null}
                    blockedReason={blockedReason}
                    isPosting={poster.isPosting}
                    onReview={openReview}
                  />
                </div>
              )
            ) : (
              <ReconciliationPanel
                rows={rows}
                columns={visibleColumns}
                amounts={amounts}
                objections={objections}
                lines={pendingLines}
                results={poster.results}
                hasPosted={poster.summary !== null}
              />
            )}
          </div>
        </div>

        {context ? (
          <PostBatchDialog
            open={reviewOpen}
            onOpenChange={(next) => {
              setReviewOpen(next)
              // Closing over a part-finished run: pick up what it settled, so the locks and the
              // "already posted" banner on the grid behind agree with the ledger.
              if (!next) {
                setJournal(readJournal(meetingId))
              }
            }}
            lines={pendingLines}
            totals={totals}
            columns={visibleColumns}
            declaredMinor={declaredMinor}
            valueDate={context.valueDate}
            businessDate={businessDate ?? context.valueDate}
            results={poster.results}
            activeKey={poster.activeKey}
            isPosting={poster.isPosting}
            summary={poster.summary}
            onConfirm={() => {
              void poster.post(pendingLines, context)
            }}
            onRetryRefused={() => {
              const refused = pendingLines.filter(
                (line) => poster.results[line.key]?.status === 'refused',
              )
              if (refused.length > 0) {
                void poster.post(refused, context)
              }
            }}
            onFinish={() => {
              setReviewOpen(false)
              setJournal(readJournal(meetingId))

              // A clean run means this sheet is done. Going back replaces the history entry so the
              // Back button does not reopen an emptied grid over balances that have already moved.
              if ((poster.summary?.refusedCount ?? 0) === 0) {
                navigate({ screen: 'groupMeetings', groupId }, true)
              }
            }}
          />
        ) : null}

        <ConfirmDialog
          open={confirmingFreshSheet}
          onOpenChange={setConfirmingFreshSheet}
          title="Start a fresh sheet for this meeting"
          description="This forgets what this browser posted against this meeting, including the keys that make a re-run safe."
          confirmLabel="Forget and start again"
          tone="danger"
          confirmationPhrase="FRESH"
          onConfirm={() => {
            clearJournal(meetingId)
            setJournal(readJournal(meetingId))
            setAmounts({})
            setDeclaredMinor(null)
            setAcknowledgedVarianceMinor(null)
            setPendingLines([])
            poster.reset()
            setConfirmingFreshSheet(false)
          }}
        >
          <Alert tone="danger">
            <AlertTitle>A line posted before could be posted again</AlertTitle>
            <AlertDescription>
              Nothing already in the ledger is undone by this — a posting is reversed through the
              ledger, not here. What is lost is the protection that stops the same money being taken
              twice. Check the members&apos; balances before re-keying anything.
            </AlertDescription>
          </Alert>
        </ConfirmDialog>
      </>
    )
  })()

  return (
    <RequiresWiderScreen
      screen="Collection capture"
      reason="A week of paper sheets is keyed down a dense grid at speed, and speed is exactly what a touch keyboard turns from this screen’s advantage into its risk."
      backTo="/collections"
      backLabel="Back to the collection worklist"
    >
      {backLink}
      {body}
    </RequiresWiderScreen>
  )
}
